import { useReducer } from "react";
import { v4 as uuid } from "uuid";
import lodash from "lodash";
import { useForm as uf, useFieldArray as ufa } from "react-hook-form";
import { comnUtils } from "@/comn/utils";

import type { UseFormWatch, Mode } from "react-hook-form";
import type { GroupControlProps } from "../components/Group";

type TControlTypes = Pick<GroupControlProps, "type">["type"];
type TFieldTypes = { type?: TControlTypes | "array" };
type TFieldExtra = { schema?: TFormFields };
type TField = Omit<GroupControlProps, "type"> & TFieldTypes & TFieldExtra;

export type TFormFieldName = string;
export type TFormFieldValue = any;
export type TFormValues = Record<TFormFieldName, TFormFieldValue>;
export type TFormSchema = { id: string; validateMode?: Exclude<Mode, "onTouched" | "all">; schema: TFormFields };
export type TFormFields = Record<string, TField>;

type UseFormProps = { defaultSchema: TFormSchema; defaultValues?: TFormValues };

export type TRule = {
    min?: any;
    max?: any;
    minLength?: any;
    maxLength?: any;
    required?: any;
    pattern?: any;
    validate?: any;
};

const getFields = (arg: any, control: any) => {
    return Object.entries(lodash.cloneDeep(arg)).reduce((prev: any, curr: any) => {
        const next = { ...prev };
        const { type } = curr[1];
        switch (type) {
            case "file": {
                next[curr[0]] = curr[1];
                next[curr[0]].name = curr[0];
                next[curr[0]].control = control;
                if (curr[1].required) {
                    next[curr[0]].validate = (data: any) => {
                        if (typeof data === "object") {
                            if (data.fileGrpUuid && Array.isArray(data.fileSaveReqDtoList)) {
                                const list = data.fileSaveReqDtoList.filter(({ fileStatCd }: any) => {
                                    return fileStatCd !== "D";
                                });
                                return list.length !== 0 || "msg.com.00005";
                            }
                        } else {
                            return "msg.com.00005";
                        }
                    };
                }

                break;
            }
            case "array": {
                next[curr[0]] = curr[1];
                next[curr[0]].name = curr[0];
                next[curr[0]].control = control;
                if (next[curr[0]].schema) {
                    next[curr[0]].schema = getFields(next[curr[0]].schema, control);
                }
                break;
            }
            case "daterange":
            case "timerange":
            //@ts-ignore
            case "datetimerange": {
                const { start, end } = curr[1];
                let childType;
                if (type === "daterange") childType = "date";
                if (type === "timerange") childType = "time";
                if (type === "datetimerange") childType = "datetime";
                start.type = childType;
                start._parent = curr[0];
                start.control = control;
                end.type = childType;
                end._parent = curr[0];
                end.control = control;
                next[start.name] = start;
                next[end.name] = end;
            }
            default: {
                next[curr[0]] = curr[1];
                next[curr[0]].name = curr[0];
                if (type !== "daterange" && type !== "timerange" && type !== "datetimerange")
                    next[curr[0]].control = control;
            }
        }
        return next;
    }, {});
};

const reducer = (state: any, { type, payload }: any) => {
    switch (type) {
        case "setSchema": {
            const nextState = { ...state };
            const { name, value } = payload;

            const target = { ...nextState[name], ...value };
            nextState[name] = target;
            if (target._parent) {
                nextState[target._parent][name] = target;
            }

            return nextState;
        }
        case "setSchemas": {
            const nextState = { ...state };
            const { names, value } = payload;
            for (const name of names) {
                const target = { ...nextState[name], ...value };
                nextState[name] = target;
                if (target._parent) {
                    nextState[target._parent][name] = target;
                }
            }
            return nextState;
        }
        case "resetSchema": {
            const { schema, arg, control } = payload;
            return getFields(arg || schema, control);
        }
        case "setEditable": {
            const nextState = { ...state };
            const { arg, value } = payload;
            if (value === undefined) {
                for (const name in nextState) {
                    const target = nextState[name];

                    if (target.type === "array") {
                        for (const arrayFieldName in nextState[name].schema) {
                            nextState[name].schema[arrayFieldName].edit = Boolean(arg);
                        }
                    } else {
                        nextState[name].edit = Boolean(arg);
                    }
                }
            }
            if (typeof arg === "string") {
                const target = nextState[arg];

                if (target.type === "array") {
                    for (const arrayFieldName in nextState[arg].schema) {
                        nextState[arg].schema[arrayFieldName].edit = Boolean(value);
                    }
                } else {
                    nextState[arg].edit = Boolean(value);
                }
            }

            return nextState;
        }
    }
};

const initializer = (arg: any) => {
    return getFields(arg.schema, arg.control);
};

export type UseFormReturn = {
    schema: any;
    handleSubmit: (onValid?: (...arg: any) => any, onInvalid?: (...arg: any) => any) => any;
    getValue: (...arg: any) => any;
    getValues: (...arg: any) => any;
    setFocus: (...arg: any) => any;
    setValue: (...arg: any) => any;
    setValues: (...arg: any) => any;
    clearValues: (...arg: any) => any;
    setSchema: (...arg: any) => any;
    setSchemas: (...arg: any) => any;
    resetSchema: (...arg: any) => any;
    setEditable: (...arg: any) => any;
    validate: (...arg: any) => any;
    clearErrors: (...arg: any) => any;
    watch: UseFormWatch<TFormValues>;
    errors: any;
    isSubmitted: any;
    isError: any;
    setError: (...arg: any) => any;
    setErrors: (...arg: any) => any;
    reset: (...arg: any) => any;
};

export const useFieldArray = (props: any) => {
    const { name, control, schema } = props;
    const { fields, append, insert, prepend, remove } = ufa({
        control,
        name,
    });

    const test12 = fields.map((data: any, index: any) => {
        const s = Object.entries<any>(schema).reduce((prev: any, curr: any) => {
            const next = { ...prev };
            const { type } = curr[1];
            switch (type) {
                case "daterange":
                case "timerange":
                case "datetimerange": {
                    const cu = { ...curr[1] };
                    const _start = { ...cu.start };
                    const _end = { ...cu.end };
                    _start.name = name + `.${index}.` + _start.name;
                    _end.name = name + `.${index}.` + _end.name;
                    cu.start = _start;
                    cu.end = _end;
                    next[curr[0]] = cu;
                    break;
                }
                default: {
                    const cu = { ...curr[1] };
                    cu.name = name + `.${index}.` + curr[1].name;
                    next[curr[0]] = cu;
                }
            }
            return next;
        }, {});
        const __key = data.id;
        return { schema: s, key: __key };
    });

    return { fields: test12, remove, append, prepend, insert };
};

export const useForm = (props: UseFormProps): UseFormReturn => {
    const { defaultSchema, defaultValues } = props;
    const { id = uuid(), schema, validateMode = "onChange" } = defaultSchema;

    const {
        control,
        getValues,
        setValue,
        setFocus,
        trigger,
        watch,
        reset,
        setError,
        handleSubmit,
        clearErrors,
        formState: { errors, isSubmitted },
    } = uf({
        mode: "onSubmit",
        criteriaMode: "all",
        reValidateMode: validateMode,
        defaultValues:
            defaultValues &&
            Object.fromEntries(
                Object.entries(defaultValues).map(([key, value]) => {
                    return [key, comnUtils.getFormattedValue(value, schema[key])];
                }),
            ),
    });

    const [fields, dispatch] = useReducer(reducer, { schema, control }, initializer);

    const setSchema = (name: string, value: any) => {
        dispatch({ type: "setSchema", payload: { name, value } });
    };
    const setSchemas = (names: string[], value: any) => {
        dispatch({ type: "setSchemas", payload: { names, value } });
    };
    const resetSchema = (arg?: any) => {
        dispatch({ type: "resetSchema", payload: { schema, arg, control } });
    };
    const setEditable = (arg: any, value?: any) => {
        dispatch({ type: "setEditable", payload: { arg, value } });
    };
    const _getValues = (arg?: string | string[]) => {
        if (typeof arg === "string") {
            return _getValue(arg);
        }

        if (Array.isArray(arg)) {
            return getValues(arg).reduce((prev: any, data: any, index: any) => {
                const name = arg[index];
                const fieldSchema = fields[name];
                const type = fieldSchema?.type;

                switch (type) {
                    case "array": {
                        const arraySchema = fieldSchema.schema;
                        const returnData = data.map((item: any) => {
                            const formatted = Object.entries(item).reduce((formattedItem: any, curr: any) => {
                                const [itemFieldName, itemFieldValue] = curr;
                                const itemFieldSchema = arraySchema[itemFieldName];
                                formattedItem[itemFieldName] =
                                    comnUtils.getUnformattedValue(itemFieldValue, itemFieldSchema) ?? undefined;
                                return formattedItem;
                            }, {});
                            return formatted;
                        });
                        prev[name] = returnData;
                        break;
                    }
                    default: {
                        prev[name] = comnUtils.getUnformattedValue(data, fieldSchema) ?? undefined;
                    }
                }

                return prev;
            }, {});
        }

        return Object.entries(getValues()).reduce((prev: any, curr: any) => {
            const [name, value] = curr;
            const type = fields[name]?.type;

            if (type === "array") {
                prev[name] = value.map((data: any) => {
                    return Object.entries(data).reduce((prev2: any, curr2: any) => {
                        prev2[curr2[0]] =
                            comnUtils.getUnformattedValue(curr2[1], fields[name].schema[curr2[0]]) ?? undefined;
                        return prev2;
                    }, {});
                });
            } else {
                prev[name] = comnUtils.getUnformattedValue(value, fields[name]) ?? undefined;
            }

            return prev;
        }, {});
    };

    const _getValue = (name: string, alt?: any) => {
        const type = fields[name]?.type;
        const data = getValues(name);

        if (type === "array") {
            const arraySchema = fields[name].schema;
            const returnData = data.map((item: any) => {
                const formatted = Object.entries(item).reduce((prev: any, curr: any) => {
                    const [itemFieldName, itemFieldValue] = curr;
                    const itemFieldSchema = arraySchema[itemFieldName];
                    prev[itemFieldName] = comnUtils.getUnformattedValue(itemFieldValue, itemFieldSchema) ?? undefined;
                    return prev;
                }, {});
                return formatted;
            });
            return returnData;
        }

        const fieldSchema = fields[name];
        const value = comnUtils.getUnformattedValue(data, fieldSchema) ?? undefined;
        if (alt && comnUtils.isEmpty(value)) return alt;
        return value;
    };
    const _setValue = (name: string, value: any) => {
        setValue(name, comnUtils.getFormattedValue(value, fields[name]), { shouldValidate: isSubmitted });
    };
    const _setValues = (values: TFormValues, _?: boolean) => {
        Object.keys(values).forEach((name) => {
            let value = values[name];
            _setValue(name, value);
        });
    };
    const _clearValues = () => {
        Object.keys(getValues()).forEach((name) => {
            setValue(name, undefined, { shouldValidate: isSubmitted });
        });
    };

    const validate = (name?: TFormFieldValue) => {
        if (name in fields) return trigger(name, { shouldFocus: true });
        else return trigger(undefined, { shouldFocus: true });
    };

    const _handleSubmit = (onValid?: (data: any) => void, onInvalid?: (data: any) => void) => {
        return handleSubmit(
            (data) => {
                if (!onValid) return;
                onValid(_getValues());
            },
            (error) => {
                if (!onInvalid) return;
                onInvalid(error);
            },
        );
    };

    const setErrors = (errors: any) => {
        if (errors === undefined || errors === null) return;
        Object.keys(fields).forEach((name) => {
            if (errors[name] === undefined || errors[name] === null) return;
            setError(name, { message: errors[name], type: "error" });
        });
    };

    const SCHEMA = Object.entries(fields).reduce((prev: any, [name, schema]: any) => {
        const next = { ...prev };
        const { type } = schema;
        const key = id + "." + name;

        if (type === "timerange" || type === "daterange" || type === "datetimerange") {
            const { start, end } = schema;
            start.control = control;
            start.invalid = errors[start.name];
            end.control = control;
            end.invalid = errors[end.name];
        } else if (type === "array") {
            // console.log(schema.schema);
        } else {
            schema.name = name;
            schema.control = control;
            schema.invalid = errors[name];
        }

        // schema.key = key;

        return { ...next, [name]: schema };
    }, {});

    return {
        schema: SCHEMA,
        handleSubmit: _handleSubmit,
        getValue: _getValue,
        getValues: _getValues,
        setFocus,
        setValue: _setValue,
        setValues: _setValues,
        clearValues: _clearValues,
        setSchema,
        setSchemas,
        resetSchema,
        setEditable,
        validate,
        clearErrors,
        watch,
        errors,
        isSubmitted,
        isError: !comnUtils.isEmpty(errors),
        setError,
        setErrors,
        reset,
    };
};
